import { type ButtonHTMLAttributes, forwardRef } from 'react'
import { useIntl } from 'react-intl'
import { useTheme } from '@mui/material/styles'
import { differenceInMinutes, format, isToday, isTomorrow, parseISO } from 'date-fns'
import { type Locale } from 'invictus-sdk-typescript'

import { HUMAN_DATE_FORMAT } from '@Constants/date'
import { getNavigationAndActionIcon } from '@DS/components/icons/navigationAndActionIcons'
import { useIsMediumUp } from '@Hooks/useResponsive'
import { specificHourFormats, specificHourMinutesFormats } from '@Utils/datepicker'
import { dateFnsLocale } from '@Utils/locale'
import { capitalizeFirstLetterOfSentence } from '@Utils/lodash'

import DateTimeButton, { type DateTimeButtonProps } from '../atoms/DateTimeButton'

import * as styles from './styles'

const AddIcon = getNavigationAndActionIcon('add')

export type DateSearchInputProps = ButtonHTMLAttributes<HTMLButtonElement> &
  Omit<DateTimeButtonProps, 'prefix' | 'disabled'>

export type DateSearchProps = {
  inwardDateInputField?: DateSearchInputProps
  outwardDateInputField: DateSearchInputProps
  hasTimeHidden?: boolean
  isCompactedOnMobile?: boolean
  hasMinutesDisplayed?: boolean
  isAlertingNow?: boolean
  isOutwardOnly?: boolean
  isElevated?: boolean
  size?: DateTimeButtonProps['size']
}

const DateSearch = forwardRef<HTMLDivElement, DateSearchProps>(
  (
    {
      inwardDateInputField,
      outwardDateInputField: {
        value: outwardDateValue,
        onClick: outwardDateClick,
        isVisible: isOutwardDateVisible = true,
        isDisabled: isOutwardDateDisabled = false,
        buttonLabelId: outwardButtonLabelId,
        ...restOutwardDate
      },
      hasTimeHidden = false,
      isCompactedOnMobile = true,
      hasMinutesDisplayed = false,
      isAlertingNow = false,
      isOutwardOnly = false,
      isElevated = false,
      size = 'medium',
      ...rest
    },
    ref,
  ) => {
    const theme = useTheme()
    const { formatMessage, locale } = useIntl()
    const lang = locale as Locale
    const isMediumUp = useIsMediumUp(theme)

    const getDateFormat = (hourFormat: string): string => {
      if (hasTimeHidden) {
        return HUMAN_DATE_FORMAT
      }

      if (isMediumUp || !isCompactedOnMobile) {
        return `iii dd MMM, ${hourFormat}`
      }

      return `dd MMM, ${hourFormat}`
    }

    const getTimeLabel = (dateToFormat: Date, hourFormat: string): string => {
      if (hasTimeHidden) {
        return ''
      }

      return `, ${format(dateToFormat, hourFormat, {
        locale: dateFnsLocale[lang],
      })}`
    }

    const getFormatedDate = (dateToFormat: Date, hourFormat: string, isForA11y: boolean): string => {
      if (isAlertingNow) {
        const diff = differenceInMinutes(dateToFormat, new Date(), { roundingMethod: 'round' })

        if (diff < 5) {
          return formatMessage({ id: 'now' })
        }
      }

      const a11yFormat = hasTimeHidden ? `dd/MM/yyyy` : `iiii dd MMMM, ${hourFormat}`
      const timeLabel = getTimeLabel(dateToFormat, hourFormat)

      if (isToday(dateToFormat) || isTomorrow(dateToFormat)) {
        return `${formatMessage({ id: isToday(dateToFormat) ? 'today' : 'tomorrow' })}${timeLabel}`
      }

      return capitalizeFirstLetterOfSentence(
        format(dateToFormat, isForA11y ? a11yFormat : getDateFormat(hourFormat), {
          locale: dateFnsLocale[lang],
        }),
      )
    }

    const getFormattedDateForA11y = (formattedDate: string, isEndDate: boolean): string => {
      const getInwardOrOutwardDateLabelId = () => {
        if (isEndDate && inwardDateInputField) {
          return inwardDateInputField.buttonLabelId ?? 'trip_inwardDateLabel'
        }

        return outwardButtonLabelId ?? 'trip_outwardDateLabel'
      }

      return formatMessage(
        { id: getInwardOrOutwardDateLabelId() },
        {
          date: formattedDate,
        },
      )
    }

    const getDisplayValue = (dateToFormat: Date, isEndDate: boolean, isForA11y = false): string => {
      const hourFormat = hasMinutesDisplayed ? specificHourMinutesFormats[lang] : specificHourFormats[lang]
      const formattedDate = getFormatedDate(dateToFormat, hourFormat, isForA11y)

      if (isForA11y) {
        return getFormattedDateForA11y(formattedDate, isEndDate)
      }

      return formattedDate
    }

    const getInwardPlaceholder = ({ buttonLabelId, placeholder }: DateSearchInputProps) => {
      if (placeholder) {
        return placeholder
      }

      return buttonLabelId
        ? formatMessage({ id: 'dateSearchCatalog_add_inward' })
        : formatMessage({ id: isMediumUp ? 'trip_addInwardLabel' : 'trip_addInwardResponsiveLabel' })
    }

    return (
      <div {...rest} ref={ref} css={styles.datesWrapper(isOutwardOnly, size)}>
        <DateTimeButton
          {...restOutwardDate}
          size={size}
          aria-label={outwardDateValue && getDisplayValue(parseISO(outwardDateValue), false, true)}
          isDisabled={isOutwardDateDisabled}
          onClick={!isOutwardDateDisabled ? outwardDateClick : () => undefined}
          css={[
            styles.inputDate(
              theme,
              inwardDateInputField?.isVisible ? 'outward' : 'singleDate',
              isOutwardDateDisabled,
              isElevated,
            ),
            isOutwardDateDisabled && styles.disabledLabel(theme),
          ]}
          buttonLabelId={outwardButtonLabelId || 'trip_outwardDateLabel'}
          value={outwardDateValue && getDisplayValue(parseISO(outwardDateValue), false)}
          placeholder={formatMessage({ id: 'leaveOn' })}
          isVisible={isOutwardDateVisible}
          isCompactedOnMobile={isCompactedOnMobile}
        />
        {inwardDateInputField && (
          <DateTimeButton
            {...inwardDateInputField}
            size={size}
            aria-label={inwardDateInputField.value && getDisplayValue(parseISO(inwardDateInputField.value), true, true)}
            buttonLabelId={inwardDateInputField.buttonLabelId || 'trip_inwardDateLabel'}
            css={[
              styles.inputDate(
                theme,
                isOutwardDateVisible && inwardDateInputField.isVisible ? 'inward' : 'singleDate',
                inwardDateInputField.isDisabled,
                isElevated,
              ),
              styles.inwardInput,
            ]}
            isCompactedOnMobile={isCompactedOnMobile}
            isDisabled={inwardDateInputField.isDisabled}
            isVisible={inwardDateInputField.isVisible}
            isWrapped={inwardDateInputField.isWrapped}
            placeholder={getInwardPlaceholder(inwardDateInputField)}
            startIcon={!inwardDateInputField.value && <AddIcon css={styles.startIcon(theme)} />}
            value={inwardDateInputField.value && getDisplayValue(parseISO(inwardDateInputField.value), true)}
            onClick={!inwardDateInputField.isDisabled ? inwardDateInputField.onClick : () => undefined}
          />
        )}
      </div>
    )
  },
)

export default DateSearch
